import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import FlyupLabel from 'common/FlyupLabel';
import useKeyboardControls from 'hooks/useKeyboardControls';
import useOutsideClick from 'hooks/useOutsideClick';
import checkboxStyles from 'styles/checkbox';
import isIE11 from 'utils/isIE11';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import buttonLinkStyles from 'styles/buttonLink';

import inputStyles from './styles/input';
import rootStyles from './styles/root';
import caretDown from './icons/caretDown.svg';

const styles = (theme) => {
  const { oaoButtons: buttons, palette, spacing, typography } = theme;
  const inputStylesOutput = inputStyles(theme);
  return {
    button: {
      ...buttonLinkStyles(theme),
    },
    checkbox: checkboxStyles(theme),
    hidden: {
      display: 'none',
    },
    input: {
      ...inputStylesOutput,
      '& .caret': {
        background: isIE11 ? `transparent url(${caretDown}) 0 50% no-repeat` : palette.icon,
        color: palette.icon,
        display: ({ loading }) => (loading ? 'none' : 'inline-block'),
        marginRight: 5,
        maskImage: isIE11 ? 'none' : `url(${caretDown})`,
        pointerEvents: 'none',
        position: 'relative',
        width: 32,
        zIndex: 2,
      },
      display: 'block',
      marginTop: 10,
    },
    inputSearch: {
      ...inputStylesOutput,
      borderBottomColor: buttons.primary.background,
      paddingLeft: spacing.unit * 2,
      paddingTop: 0,
      zIndex: 10,
    },
    list: {
      '& .selected': {
        background: buttons.primary.background,
        color: buttons.primary.color,
      },
      '& li': {
        '& button': {
          color: typography.p2.color,
          lineHeight: `${spacing.sm}px`,
          marginLeft: spacing.unit * 3,
          textDecoration: 'none',
          verticalAlign: 'middle',
        },
        marginTop: spacing.unit,
        padding: [0, spacing.unit * 2],
      },
      background: palette.background,
      borderRadius: 2,
      fontFamily: typography.fontFamilies.secondary,
      height: 200,
      marginLeft: 0,
      marginTop: 2,
      overflowY: 'scroll',
      padding: [spacing.unit, 0],
      zIndex: 10,
    },
    listWrap: {
      '& item-list': {
        minWidth: '100%',
      },
      '&.hidden': {
        display: 'none',
      },
      background: palette.background,
      border: `1px solid ${palette.form.border}`,
      borderTop: 'none',
      bottom: 'auto',
      padding: [0, spacing.unit * 2],
      top: 'auto',
    },
    root: rootStyles(theme),
    selected: {
      '& ul': {
        '& li': {
          '&::after': {
            content: "', '",
            marginRight: 4,
          },
          '&:last-of-type': {
            '&::after': {
              content: 'none',
            },
          },
          display: 'inline-block',
        },
        fontSize: 18,
        margin: 0,
        overflow: 'hidden',
        padding: [10, 0],
        paddingLeft: 0,
        position: 'absolute',
        textOverflow: 'ellipsis',
        top: spacing.unit,
        whiteSpace: 'nowrap',
        width: '100%',
      },
    },
    show: {
      display: 'block',
    },
    tooltip: {
      display: 'block',
      height: ({ value }) => (value && value.split(',').length * 30) || 0,
      position: 'absolute',
      top: '135%',
    },
  };
};

const MultiSelectBare = ({
  classes,
  displayOrder,
  error,
  fieldDomId,
  help,
  maxShown,
  name,
  onChange,
  options,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => {
  const lookupOrder = displayOrder === undefined ? Object.keys(options) : displayOrder;
  const valArray = (value && value.split(',')) || [];
  const [showOptions, setShowOptions] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const outsideNode = useOutsideClick(() => setShowOptions(false));
  const searchRef = useRef();
  const wrapperClick = useCallback(() => {
    if (readOnly) {
      return;
    }
    setShowOptions((option) => !option);
  }, [readOnly]);
  useEffect(() => {
    if (showOptions) {
      searchRef.current.focus();
    }
  }, [showOptions]);
  const optionKeys = useMemo(() => {
    const keys = lookupOrder;
    if (!searchValue) {
      return keys;
    }
    const searchRegex = new RegExp(searchValue, 'gi');
    return keys.filter((o) => searchRegex.test(options[o]));
  }, [lookupOrder, options, searchValue]);

  const [selectedIndex, events] = useKeyboardControls({
    enterCallback: (choices, index) => {
      onChange(
        name,
        valArray.includes(choices[index])
          ? valArray.filter((v) => v !== choices[index]).join(',')
          : valArray.concat(choices[index]).join(','),
      );
      validate();
    },
    options: optionKeys,
    resetIndex: false,
  });

  return (
    <div ref={outsideNode} className={classnames('form-group', { error }, classes.root)}>
      <FlyupLabel
        fieldName={name}
        value={value || showOptions}
        focus={false}
        htmlFor={fieldDomId}
        className="label-wrap click-through"
        tooltip={tooltip}
        required={required}
      />
      <div className="region-flex-dropdown">
        <div className="form-group flex-dropdown-wrapper">
          <div className="flex-dropdown open max-width">
            <div className="flex-wrapper">
              <span
                role="button"
                tabIndex="0"
                className={classnames('selection', classes.input)}
                onClick={wrapperClick}
                onKeyDown={() => {
                  if (showOptions) {
                    return;
                  }
                  wrapperClick();
                }}
              >
                <div className={classnames('selected', classes.selected)}>
                  <div>
                    {valArray.length > 0 && (
                      <>
                        {valArray.length <= maxShown ? (
                          <ul className={classnames('selected-items')} style={{ display: 'block' }}>
                            {valArray.map((val) => (
                              <li key={`selected-${val}`}>
                                <span>{options[val]}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <ul className={classnames('total-selected')} style={{ display: 'block' }}>
                            <li>
                              <span>{valArray.length}</span>
                              {' selected'}
                            </li>
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="line" />
                <span className="caret" />
              </span>
              <div className={classnames('list', classes.listWrap, { hidden: !showOptions })}>
                <input
                  {...events}
                  ref={searchRef}
                  type="text"
                  className={classnames('form-control', classes.inputSearch)}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <div className="filter-autocomplete hidden">
                  <span className="filter-text text-nowrap" />
                  <span className="highlighted-autocomplete text-nowrap" />
                  <span className="preselected-value text-nowrap" />
                </div>
                <div className="items">
                  <div>
                    <div className="region-custom-view custom-view" />
                    <div className="region-items items-view">
                      <div>
                        <ul className={classnames('item-list', classes.list)}>
                          {optionKeys.map((key, i) => (
                            /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
                            <li
                              key={`option-${key}`}
                              className={classnames('selectable', {
                                selected: selectedIndex === i,
                              })}
                              onClick={(e) => {
                                if (e.target.tagName !== 'INPUT') {
                                  e.preventDefault();
                                }
                                onChange(
                                  name,
                                  valArray.includes(key)
                                    ? valArray.filter((v) => v !== key).join(',')
                                    : valArray.concat(key).join(','),
                                );
                                validate();
                              }}
                            >
                              <input
                                type="checkbox"
                                value={key}
                                checked={valArray.includes(key) || false}
                                onChange={() => {}}
                                className={classes.checkbox}
                                aria-describedby={
                                  classnames({
                                    [`help-${name}`]: help,
                                    [`error-${name}`]: error,
                                  }) || undefined
                                }
                              />
                              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                              <button
                                type="button"
                                className={classnames('buttonLink', classes.button)}
                              >
                                <span>{options[key]}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                        <div className="button-holder" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <i className="handle icon-caret-down" aria-hidden="true" />

      <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
      <Help help={help} id={`help-${name}`} />

      <ErrorMsg name={name} />
    </div>
  );
};

MultiSelectBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  displayOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  maxShown: PropTypes.number,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({}).isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

MultiSelectBare.defaultProps = {
  maxShown: 4,
};

const MultiSelectStyled = withStyles(styles)(MultiSelectBare);

const mapStateToProps = ({ fields }, { name }) => {
  const field = fields[name];
  return {
    displayOrder: fields[name].lookupOrder || Object.keys(fields[name].lookupValues),
    options: field.lookupValues,
  };
};

export default withFieldProps(mapStateToProps)(MultiSelectStyled);
